import { useDisclosure } from '@mantine/hooks';
import { useCallback, useState } from 'react';
import type { Callback } from 'ts/base/Callback';
import { useCommit } from 'ts/base/hooks/UseCommit';
import { useKeyboardShortcut } from 'ts/base/hooks/UseKeyboardShortcut';
import { useProjectId } from 'ts/base/hooks/UseProject';
import { HelpIcon } from 'ts/commons/components/HelpIcon';
import { DateUtils } from 'ts/commons/DateUtils';
import { Button } from 'ts/components/Button';
import { Message } from 'ts/components/Message';
import { ExclusionRationaleModal } from 'ts/perspectives/findings/details/tabs/commons/ExclusionRationaleModal';
import { ResolvePendingExclusionRationaleModal } from 'ts/perspectives/findings/details/tabs/commons/ResolvePendingExclusionRationaleModal';
import { useBlacklistPermissions } from 'ts/perspectives/findings/details/tabs/commons/UseBlacklistPermissions';
import { useBlacklistMutation } from 'ts/perspectives/findings/details/tabs/FindingDetailsSiblingsTabBlacklistButtons';
import { BlacklistIcon } from 'ts/perspectives/findings/list/FindingsTable.columns';
import { RoundAvatar } from 'ts/perspectives/user/RoundAvatar';
import type { Approved } from 'typedefs/Approved';
import { EFindingBlacklistOperation } from 'typedefs/EFindingBlacklistOperation';
import { EFindingBlacklistType } from 'typedefs/EFindingBlacklistType';
import type { IApprovalState } from 'typedefs/IApprovalState';
import type { Pending } from 'typedefs/Pending';
import type { TrackedFindingWithDiffInfo } from 'typedefs/TrackedFindingWithDiffInfo';
import type { UserResolvedFindingBlacklistInfo } from 'typedefs/UserResolvedFindingBlacklistInfo';

/** Renders buttons to mark findings as tolerated or false positives. */
export function FlaggingButtons({
	finding,
	onDialogClose
}: {
	finding: TrackedFindingWithDiffInfo;
	onDialogClose: Callback<void>;
}) {
	const { canFlagAsToleration, canFlagAsFalsePositive } = useBlacklistPermissions([finding]);
	const commit = useCommit();
	const [rationaleModalOpened, { open, close }] = useDisclosure(false);
	const [flaggingType, setFlaggingType] = useState(EFindingBlacklistType.FALSE_POSITIVE);

	const openModalWithFlaggingType = useCallback(
		(type: EFindingBlacklistType) => {
			setFlaggingType(type);
			open();
		},
		[open]
	);

	useKeyboardShortcut(
		'F',
		'Mark as False Positive',
		() => openModalWithFlaggingType(EFindingBlacklistType.FALSE_POSITIVE),
		canFlagAsFalsePositive
	);
	useKeyboardShortcut(
		'T',
		'Tolerate',
		() => openModalWithFlaggingType(EFindingBlacklistType.TOLERATION),
		canFlagAsToleration
	);

	return (
		<div className="flex flex-wrap w-fit gap-x-1 gap-y-2 justify-end">
			{canFlagAsFalsePositive ? (
				<Button
					compact
					title="Marks this finding as False Positive"
					data-testid="false-positive-button"
					icon="false positive"
					content="False Positive"
					onClick={() => openModalWithFlaggingType(EFindingBlacklistType.FALSE_POSITIVE)}
					className="!mr-0"
				/>
			) : null}
			{canFlagAsToleration ? (
				<Button
					compact
					title="Marks this finding as Tolerated"
					data-testid="tolerate-button"
					icon="toleration"
					content="Tolerate"
					onClick={() => openModalWithFlaggingType(EFindingBlacklistType.TOLERATION)}
				/>
			) : null}
			{rationaleModalOpened ? (
				<ExclusionRationaleModal
					onClose={() => {
						onDialogClose();
						close();
					}}
					exclusionType={flaggingType}
					findingsToExclude={[finding]}
					commit={commit}
					updateQueriesOptimistically={false}
				/>
			) : null}
		</div>
	);
}

type FlaggingInfoProps = {
	finding: TrackedFindingWithDiffInfo;
	blacklistInfo: UserResolvedFindingBlacklistInfo;
	/**
	 * Whether the blacklist commit hasn't been analyzed yet, which means the blacklisted finding will still influence
	 * metrics etc.
	 */
	integrationPending: boolean;
	onFlaggingChange?: Callback<void>;
};

/** Whether the provided approvalState is Approved */
function isApproved(approvalState: IApprovalState): approvalState is Approved {
	return 'approved' in approvalState && approvalState.approved;
}

/** Whether the provided approvalState is Pending */
export function isPending(approvalState: IApprovalState): approvalState is Pending {
	return !approvalState.resolved;
}

/** Shows flagging info of findings that were marked as tolerated or false positive. */
export function ExclusionInfo({ finding, blacklistInfo, integrationPending, onFlaggingChange }: FlaggingInfoProps) {
	const isFalsePositive = blacklistInfo.type === EFindingBlacklistType.FALSE_POSITIVE.name;

	return (
		<div data-testid="exclusion-info">
			<h4 className="mb-2">
				<BlacklistIcon blacklistInfo={blacklistInfo} />
				<span data-testid="exclusion-name">{isFalsePositive ? 'Marked as False Positive' : 'Tolerated'}</span>
			</h4>
			<ExcludedByInfo blacklistInfo={blacklistInfo} />
			<Message data-testid="exclusion-rationale" size="small" className="overflow-x-auto">
				{blacklistInfo.rationale || <i>(no rationale)</i>}
			</Message>
			{integrationPending ? (
				<div className="inline-block font-normal muted ml-2">
					Waiting for {blacklistInfo.updateType === 'REMOVE' ? 'removal' : 'integration'}{' '}
					<HelpIcon
						align="end"
						side="bottom"
						textOrMarkdown="This finding exclusion has not been fully integrated yet, so finding-related metrics aren't up-to-date."
					/>
				</div>
			) : (
				<ExclusionActionButtons
					finding={finding}
					blacklistInfo={blacklistInfo}
					onExclusionChange={onFlaggingChange}
				/>
			)}
		</div>
	);
}

function ExcludedByInfo({ blacklistInfo }: { blacklistInfo: UserResolvedFindingBlacklistInfo }) {
	return (
		<span className="muted description">
			by{' '}
			<span data-testid="excluded-by">
				<RoundAvatar user={blacklistInfo.resolvedUser} username={blacklistInfo.user} />
			</span>{' '}
			on <span data-testid="excluded-on">{DateUtils.formatTimestamp(blacklistInfo.timestamp)}</span>
			{isApproved(blacklistInfo.approvalState) && blacklistInfo.approvalState.at !== blacklistInfo.timestamp ? (
				<>
					<br />
					approved by{' '}
					<span data-testid="approved-by">
						<RoundAvatar user={blacklistInfo.resolvedApprover} username={blacklistInfo.approvalState.by} />
					</span>{' '}
					on{' '}
					<span data-testid="approved-on">{DateUtils.formatTimestamp(blacklistInfo.approvalState.at)}</span>
				</>
			) : null}
		</span>
	);
}

type ExclusionActionButtonsProps = {
	finding: TrackedFindingWithDiffInfo;
	blacklistInfo: UserResolvedFindingBlacklistInfo;
	onExclusionChange?: Callback<void>;
};

function ExclusionActionButtons({ finding, blacklistInfo, onExclusionChange }: ExclusionActionButtonsProps) {
	const permissions = useBlacklistPermissions([finding], [blacklistInfo]).detailedPermissions[blacklistInfo.type];
	const props: ActionButtonProps = {
		finding,
		blacklistInfo,
		onExclusionChange
	};
	return (
		<div className="flex flex-wrap justify-end gap-y-2">
			{permissions.includes('Unflag') ? <UnflagButton {...props} /> : null}
			{permissions.includes('Approve') ? (
				<>
					<ApproveExclusionButton {...props} />
					<RejectExclusionButton {...props} />
				</>
			) : null}
		</div>
	);
}

type ActionButtonProps = ExclusionActionButtonsProps;

function useBlacklistDeletion(blacklistInfo: UserResolvedFindingBlacklistInfo, onExclusionChange?: Callback<void>) {
	const project = useProjectId();
	const commit = useCommit();
	const mutation = useBlacklistMutation([blacklistInfo.findingId], commit);
	return () => {
		mutation.mutate(
			{
				pathParams: { project },
				queryParams: {
					t: commit,
					operation: EFindingBlacklistOperation.REMOVE.name,
					type: blacklistInfo.type
				},
				body: {
					findingIds: [blacklistInfo.findingId]
				}
			},
			{
				onSettled: () => onExclusionChange?.()
			}
		);
	};
}

function UnflagButton({ blacklistInfo, onExclusionChange }: ActionButtonProps) {
	const isFalsePositive = blacklistInfo.type === EFindingBlacklistType.FALSE_POSITIVE.name;
	const onClick = useBlacklistDeletion(blacklistInfo, onExclusionChange);
	return (
		<Button
			compact
			data-testid="unflag-button"
			title={`Removes this finding from ${isFalsePositive ? 'False Positives' : 'Tolerated'}`}
			onClick={onClick}
		>
			{isApproved(blacklistInfo.approvalState)
				? `Remove from ${isFalsePositive ? 'False Positives' : 'Tolerated'}`
				: `Remove from Pending ${EFindingBlacklistType.valueOf(blacklistInfo.type).readableName}s`}
		</Button>
	);
}

function ApproveExclusionButton(props: ActionButtonProps) {
	return <ResolveExclusionSuggestionButton {...props} approve />;
}

function RejectExclusionButton(props: ActionButtonProps) {
	return <ResolveExclusionSuggestionButton {...props} approve={false} />;
}

function ResolveExclusionSuggestionButton({
	blacklistInfo,
	onExclusionChange,
	approve
}: ActionButtonProps & {
	approve: boolean;
}) {
	const [opened, { close, open }] = useDisclosure(false);
	const commit = useCommit();
	return (
		<>
			<Button compact onClick={open} data-testid={approve ? 'approve-button' : 'reject-button'}>
				{approve ? 'Approve as' : 'Reject'} {EFindingBlacklistType.valueOf(blacklistInfo.type).readableName}
			</Button>
			{opened ? (
				<ResolvePendingExclusionRationaleModal
					findingIds={[blacklistInfo.findingId]}
					commit={commit}
					approve={approve}
					onClose={() => {
						close();
						onExclusionChange?.();
					}}
				/>
			) : null}
		</>
	);
}
